import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { State } from '../../../../store';
import { GET_USER_DATA_URL, USER_EDIT_DATA_URL } from '../../../../api/apiRoutes';
import useApiRequest from '../../../../../utils/hooks/useApiRequest';
import useFacility from '../../../../../utils/hooks/useFacility';
import { emailValidator, maxLengthRule, PERSON_NAME_MAX_LENGTH } from '../../../../../utils/formsValidation';
import inputsValidation from '../../../../../messages/inputValidation';
import { useTranslation } from 'react-i18next';
import EATextField from '../../../shared/TextField/EATextField';
import EAPhoneInput from '../../../shared/PhoneInput/EAPhoneInput';
import EAButton from '../../../shared/Buttons/EAButton';
import EAAlert from '../../../shared/EAAlert';
import './SettingsTab.scss';
import { getUserDataByToken } from '../../../../store/auth/authActions';
import { setSuccessMessage } from '../../../../store/alerts/alertsActions';
import { errorMessageFromApi } from '../../../../utils/apiErrorMessages';

type AccountDataInputs = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

const AccountDataForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { post } = useApiRequest();
  const facilityIds = useFacility();

  const userData = useSelector((state: State) => state.auth.userData);
  const canCustomerEditData = useSelector((state: State) => state.config.configuration.canCustomerEditData);

  const [isEditing, setIsEditing] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<AccountDataInputs>({
    defaultValues: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      phone: userData.phone,
    },
  });

  const handleEditToggle = () => {
    if (!isEditing) {
      setIsEditing(true);
    } else {
      reset();
      setIsEditing(false);
      setSubmitError('');
    }
  };

  const handleSave: SubmitHandler<AccountDataInputs> = async (data) => {
    setSubmitError('');
    const response = await post(USER_EDIT_DATA_URL(facilityIds), {
      ...userData,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
    });

    if (!response.isError) {
      dispatch(setSuccessMessage(t('operacja_przebiegla_pomyslnie')));
      setIsEditing(false);
      dispatch(getUserDataByToken(GET_USER_DATA_URL(facilityIds)));
    } else {
      setSubmitError(errorMessageFromApi(response) || t('blad_aktualizacji_danych'));
    }
  };

  return (
    <div className="settings-section">
      <div className="settings-section__header">
        <h5>{t('dane_konta')}</h5>
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit(handleSave)}>
          <div className="settings-section__content">
            <EATextField
              label={t('imie')}
              name="firstName"
              type="text"
              fullWidth
              error={!!errors.firstName}
              helperText={inputsValidation(errors, 'firstName')}
              inputMaxLength={PERSON_NAME_MAX_LENGTH}
              register={register('firstName', { required: true, ...maxLengthRule(PERSON_NAME_MAX_LENGTH) })}
            />

            <EATextField
              label={t('nazwisko')}
              name="lastName"
              type="text"
              fullWidth
              error={!!errors.lastName}
              helperText={inputsValidation(errors, 'lastName')}
              inputMaxLength={PERSON_NAME_MAX_LENGTH}
              register={register('lastName', { required: true, ...maxLengthRule(PERSON_NAME_MAX_LENGTH) })}
            />

            <EATextField
              label="E-mail"
              name="email"
              type="email"
              fullWidth
              error={!!errors.email}
              helperText={inputsValidation(errors, 'email')}
              register={register('email', { ...emailValidator(true) })}
            />

            <EAPhoneInput
              control={control}
              name="phone"
              errors={errors}
              label={t('telefon_kontaktowy')}
              required
              fullWidth
            />

            {submitError && <EAAlert message={submitError} type="error" compact />}

            <div className="settings-section__actions">
              <EAButton
                title={t('zapisz')}
                onClick={handleSubmit(handleSave)}
                variant="filled"
              />
              <EAButton
                title={t('anuluj')}
                onClick={handleEditToggle}
                variant="outlined"
              />
            </div>
          </div>
        </form>
      ) : (
        <div className="settings-section__content"> 
          <div className="settings-section__info">
            <div>
              <span>{t('imie')}:</span>{' '}
              <b>{userData.firstName}</b>
            </div>
            <div>
              <span>{t('nazwisko')}:</span>{' '}
              <b>{userData.lastName}</b>
            </div>
            <div>
              <span>E-mail:</span> <b>{userData.email}</b>
            </div>
            <div>
              <span>{t('telefon_kontaktowy')}:</span>{' '}
              <b>{userData.phone}</b>
            </div>
          </div>

          {canCustomerEditData && (
            <div className="settings-section__actions">
              <EAButton
                title={t('edytuj')}
                onClick={handleEditToggle}
                variant="outlined"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountDataForm;
